import { KeyboardEvent, useMemo, useState } from 'react';
import { TagIcon, XMarkIcon } from '@heroicons/react/24/solid';

type TagChipsInputProps = {
  value?: string | null;
  onChange: (csv: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

function parseTags(value?: string | null): string[] {
  if (!value?.trim()) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of value.split(',')) {
    const tag = part.trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(tag);
  }
  return result;
}

function toCsv(tags: string[]): string {
  return tags.join(', ');
}

export function TagChipsInput({
  value,
  onChange,
  placeholder = 'Digite e pressione Enter ou vírgula',
  disabled = false,
  className = '',
}: TagChipsInputProps) {
  const tags = useMemo(() => parseTags(value), [value]);
  const [draft, setDraft] = useState('');

  function commit(next: string[]): void {
    onChange(toCsv(next));
  }

  function addTag(raw: string): void {
    const tag = raw.trim().replace(/,+$/, '').trim();
    if (!tag) {
      setDraft('');
      return;
    }
    const key = tag.toLowerCase();
    if (tags.some((item) => item.toLowerCase() === key)) {
      setDraft('');
      return;
    }
    setDraft('');
    commit([...tags, tag]);
  }

  function removeTag(tag: string): void {
    commit(tags.filter((item) => item !== tag));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
      return;
    }
    if (event.key === 'Backspace' && !draft && tags.length > 0) {
      event.preventDefault();
      removeTag(tags[tags.length - 1]);
    }
  }

  return (
    <div
      className={`mt-1 flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-lg border border-secondary-300 bg-white px-2 py-1.5 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 ${
        disabled ? 'cursor-not-allowed bg-secondary-50 opacity-70' : ''
      } ${className}`.trim()}
    >
      {tags.map((tag) => (
        <span
          key={tag.toLowerCase()}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-secondary-300 bg-white py-0.5 pl-1.5 pr-1 text-sm text-secondary-800"
        >
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <TagIcon className="h-3 w-3" aria-hidden />
          </span>
          <span className="min-w-0 truncate">{tag}</span>
          {!disabled ? (
            <button
              type="button"
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-secondary-500 hover:bg-secondary-100 hover:text-secondary-800"
              onClick={() => removeTag(tag)}
              aria-label={`Remover tag ${tag}`}
              title="Remover"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </span>
      ))}
      {!disabled ? (
        <input
          type="text"
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm text-secondary-900 outline-none placeholder:text-secondary-400"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (draft.trim()) addTag(draft);
          }}
          placeholder={tags.length === 0 ? placeholder : 'Nova tag…'}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}
